import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { deepEqual } from 'lib/utils'
import {
  decodeJson,
  encodeJson,
  QueryParamConfig,
} from 'use-query-params'

export type RawDataGridTableSettings = {
  paginationModel: GridPaginationModel | null
  sortModel: GridSortModel | null
  filterModel: GridFilterModel | null
  columnOrder: string[] | null
  columnVisibilityModel: GridColumnVisibilityModel | null
  savedReportId: string | null
  savedReportName: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validatePaginationModel(
  value: unknown
): GridPaginationModel | null {
  if (!isRecord(value)) return null

  const { page, pageSize } = value

  if (
    !Number.isInteger(page) ||
    !Number.isInteger(pageSize) ||
    (page as number) < 0 ||
    (pageSize as number) <= 0
  ) {
    return null
  }

  return {
    page: page as number,
    pageSize: pageSize as number,
  }
}

function validateSortModel(value: unknown): GridSortModel | null {
  if (!Array.isArray(value)) return null

  const isValid = value.every((item) => {
    if (!isRecord(item)) return false
    if (typeof item.field !== 'string') return false

    return (
      item.sort === 'asc' ||
      item.sort === 'desc'
    )
  })

  return isValid ? value as GridSortModel : null
}

function validateFilterModel(value: unknown): GridFilterModel | null {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    return null
  }

  const itemsAreValid = value.items.every((item) => isRecord(item))

  if (!itemsAreValid) {
    return null
  }

  return {
    ...value,
    items: value.items as GridFilterModel['items'],
  }
}

function validateColumnOrder(value: unknown): string[] | null {
  if (
    Array.isArray(value) &&
    value.every((column) => typeof column === 'string')
  ) {
    return value
  }

  return null
}

function validateColumnVisibilityModel(
  value: unknown
): GridColumnVisibilityModel | null {
  if (!isRecord(value)) return null

  const isValid = Object.values(value).every(
    (visible) => typeof visible === 'boolean'
  )

  return isValid
    ? value as GridColumnVisibilityModel
    : null
}

function validateNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0
    ? value
    : null
}

export const DataGridTableSettingsAdapter: QueryParamConfig<RawDataGridTableSettings> = {
  encode: (settings) => {
    return encodeJson(settings)
  },

  decode: (encoded) => {
    const decoded = decodeJson(encoded)

    if (!isRecord(decoded)) {
      return {
        paginationModel: null,
        sortModel: null,
        filterModel: null,
        columnOrder: null,
        columnVisibilityModel: null,
        savedReportId: null,
        savedReportName: null,
      }
    }

    return {
      paginationModel: validatePaginationModel(
        decoded.paginationModel
      ),
      sortModel: validateSortModel(decoded.sortModel),
      filterModel: validateFilterModel(decoded.filterModel),
      columnOrder: validateColumnOrder(decoded.columnOrder),
      columnVisibilityModel: validateColumnVisibilityModel(
        decoded.columnVisibilityModel
      ),
      savedReportId: validateNullableString(
        decoded.savedReportId
      ),
      savedReportName: validateNullableString(
        decoded.savedReportName
      ),
    }
  },

  equals: deepEqual,
}
